"use client";

import { SiteLink } from "@i18n/SiteLink";
import { Section } from "@marketing/shared/components/bento/Bento";
import { Reveal } from "@marketing/shared/components/bento/motion";
import { BillingToggle } from "@saas/payments/components/pricing-v2/BillingToggle";
import type { BillingInterval } from "@saas/payments/lib/pricing-v2";
import { ArrowLeftIcon, CheckIcon } from "lucide-react";
import { useState } from "react";
import { AddOnsSection } from "./AddOnsSection";

const HOW_IT_WORKS = [
	"Add them to any property, on any plan",
	"Save 10% quarterly or 20% yearly, the same as plans",
	"Results show up in the same dashboards and reports",
];

/**
 * /pricing/add-ons (English only): Perplexity daily tracking in three tiers
 * that follow the billing toggle, Claude on request, and the platform
 * coverage table. Kept off the main pricing page so the plans read as
 * complete on their own.
 */
export function AddOnsPage() {
	const [billing, setBilling] = useState<BillingInterval>("month");

	return (
		<div className="min-h-screen bg-background">
			<Section className="pt-8 lg:pt-12">
				<SiteLink
					href="/pricing"
					className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
				>
					<ArrowLeftIcon className="size-3.5" aria-hidden />
					Back to pricing
				</SiteLink>
				<Reveal>
					<div className="mx-auto mt-6 max-w-2xl text-center">
						<p className="text-sm font-bold uppercase tracking-wide text-primary">
							Add-ons
						</p>
						<h1 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl">
							Add Perplexity and Claude to your tracking
						</h1>
						<p className="mt-4 text-lg text-muted-foreground">
							Every plan tracks ChatGPT, Gemini and Google AI
							Overviews daily. Add-ons bring more AI platforms
							into the same reports, one property at a time.
						</p>
						<BillingToggle
							className="mt-6 justify-center"
							value={billing}
							onChange={setBilling}
						/>
					</div>
				</Reveal>

				<AddOnsSection className="mt-12" interval={billing} />

				<ul className="mt-8 flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6">
					{HOW_IT_WORKS.map((item) => (
						<li key={item} className="flex items-center gap-1.5">
							<CheckIcon
								className="size-4 shrink-0 text-primary"
								aria-hidden
							/>
							{item}
						</li>
					))}
				</ul>

				<p className="mt-10 text-center text-sm text-muted-foreground">
					Still choosing a plan?{" "}
					<SiteLink
						href="/pricing"
						className="font-bold text-primary hover:underline"
					>
						Compare plans
					</SiteLink>
				</p>
			</Section>
		</div>
	);
}
